import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@shared/theme/ThemeContext';

interface PublicLayoutProps {
  children: React.ReactNode;
  organizationName?: string;
  organizationLogoUrl?: string;
  workspaceName?: string;
  workspaceLogoUrl?: string;
  showFooter?: boolean;
  /** Override the post-login destination. If omitted, auto-detected from the current URL. */
  loginReturnUrl?: string;
  /** Server-authoritative auth signal. When provided, takes precedence over
   *  the local-storage fallback for deciding whether to show the Login/Signup
   *  prompt in the footer. Pages whose API response includes a
   *  `viewerIsAuthenticated` field should pass it through here so a
   *  stale/expired token can't suppress the prompt. */
  viewerIsAuthenticated?: boolean;
  backHref?: string;
  backLabel?: string;
  /**
   * How wide the header, content and footer columns run. 'default' is the
   * reading width every detail page uses; 'wide' suits calendars; 'full'
   * lets report tables and matrices use the whole viewport.
   */
  contentWidth?: 'default' | 'wide' | 'full';
}

const CONTENT_WIDTH_CLASS: Record<NonNullable<PublicLayoutProps['contentWidth']>, string> = {
  default: 'max-w-5xl',
  wide: 'max-w-7xl',
  full: 'max-w-none',
};

/** Maps a public URL path to its authenticated counterpart. */
function mapPublicUrlToAuthUrl(path: string): string {
  const billMatch = path.match(/\/public\/(?:bills|bill-preview)\/([^/?]+)/);
  if (billMatch) return `/legislation/${billMatch[1]}`;

  const legislatorMatch = path.match(/\/public\/(?:legislators|legislator-preview)\/([^/?]+)/);
  if (legislatorMatch) return `/legislators/${legislatorMatch[1]}`;

  const candidateMatch = path.match(/\/public\/candidates\/([^/?]+)/);
  if (candidateMatch) return `/candidates/${candidateMatch[1]}`;

  return '/dashboard';
}

/**
 * PublicLayout - Shared layout for all public-facing pages
 *
 * Features:
 * - Sticky header with organization/workspace branding (left logo, right name)
 * - Scrollable main content area (max-w-5xl centered)
 * - Footer with account signup prompt and Capitol Commons branding (scrolls with content)
 */
const PublicLayout: React.FC<PublicLayoutProps> = ({
  children,
  organizationName,
  organizationLogoUrl,
  workspaceName,
  workspaceLogoUrl,
  showFooter = true,
  loginReturnUrl,
  viewerIsAuthenticated,
  backHref,
  backLabel,
  contentWidth = 'default',
}) => {
  const location = useLocation();
  const widthClass = CONTENT_WIDTH_CLASS[contentWidth];

  // When this page is embedded in another site's chrome (e.g. the marketing
  // /view/public/* iframe wrapper), suppress our own sticky header so the host
  // provides the only header. Driven by an explicit ?embed=1 param, with an
  // iframe-detection fallback so it still works if the param is absent.
  const isEmbedded =
    new URLSearchParams(location.search).get('embed') === '1' ||
    (typeof window !== 'undefined' && window.self !== window.top);

  // Use workspace branding if available, otherwise fall back to organization
  const displayName = workspaceName || organizationName || 'Capitol Commons';
  const logoUrl = workspaceLogoUrl || organizationLogoUrl;

  const resolvedReturnUrl = loginReturnUrl ?? mapPublicUrlToAuthUrl(location.pathname);
  const loginHref = `/login?returnUrl=${encodeURIComponent(resolvedReturnUrl)}`;

  // Hide the Login/Signup prompt for users who already have a session — they
  // get nothing useful from being asked to sign in again. Prefer the
  // server-authoritative `viewerIsAuthenticated` prop when the page has one
  // (e.g. PublicBillDetailPage echoes the value from its API response). Fall
  // back to a localStorage check otherwise — purely cosmetic, so a stale
  // token incorrectly hiding the prompt is acceptable when no better signal
  // is available.
  const isAuthenticated =
    typeof viewerIsAuthenticated === 'boolean'
      ? viewerIsAuthenticated
      : typeof window !== 'undefined' && Boolean(window.localStorage.getItem('token'));

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-background-light dark:bg-background-dark flex flex-col">
        {/* Sticky Header — hidden when embedded so the host supplies the header */}
        {!isEmbedded && (
          <header className="sticky top-0 z-50 bg-white dark:bg-background-dark-secondary border-b border-border-light dark:border-border-dark">
            <div className={`${widthClass} mx-auto px-4 sm:px-6 lg:px-8`}>
              <div className="flex justify-between items-center py-4">
                {/* Left: Logo */}
                <div className="flex items-center">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={`${displayName} logo`}
                      className="h-10 w-auto object-contain"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center">
                      <span className="text-primary-600 dark:text-primary-400 font-bold text-lg">
                        {displayName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Right: Organization/Workspace Name */}
                <div className="text-right">
                  <h1 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                    {displayName}
                  </h1>
                </div>
              </div>
            </div>
          </header>
        )}

        {/* Main Content */}
        <main className="flex-1">
          <div className={`${widthClass} mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8`}>
            {backHref && backLabel && (
              <Link
                to={backHref}
                className="mb-4 inline-flex min-h-11 items-center text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
              >
                {backLabel}
              </Link>
            )}
            {children}
          </div>
        </main>

        {/* Footer (scrolls with content) */}
        {showFooter && (
          <footer className="bg-white dark:bg-background-dark-secondary border-t border-border-light dark:border-border-dark mt-auto">
            <div className={`${widthClass} mx-auto px-4 sm:px-6 lg:px-8 py-6`}>
              {/* Login/Signup Prompt — anonymous visitors only */}
              {!isAuthenticated && (
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-4">
                  <Link
                    to={loginHref}
                    className="px-4 py-2 border border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 text-sm font-medium rounded-lg transition-colors"
                  >
                    Login
                  </Link>
                  <span className="text-text-tertiary dark:text-text-dark-tertiary text-sm">or</span>
                  <Link
                    to="/citizen/signup"
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Sign up for more details
                  </Link>
                </div>
              )}

              {/* Copyright */}
              <div
                className={`flex items-center justify-end text-xs text-text-tertiary dark:text-text-dark-tertiary ${
                  isAuthenticated ? '' : 'pt-4 border-t border-border-light dark:border-border-dark'
                }`}
              >
                <span>© {new Date().getFullYear()} Capitol Commons PBC</span>

                {/* Right: Capitol Commons Logo */}
                <a
                  href="https://capitolcommons.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                >
                  <span className="text-text-tertiary dark:text-text-dark-tertiary">
                    Powered by
                  </span>
                  <div className="w-6 h-6 bg-primary-700 rounded flex items-center justify-center">
                    <span className="text-white text-xs font-bold">CC</span>
                  </div>
                </a>
              </div>
            </div>
          </footer>
        )}
      </div>
    </ThemeProvider>
  );
};

export default PublicLayout;
